import React, { useEffect, useRef, useState } from "react";

const LLMsAnimation = () => {
    const containerRef = useRef(null);
    const scrollRef = useRef(null);

    const [visible, setVisible] = useState(false);
    const [clickedCard, setClickedCard] = useState(null);
    const [showTextAI, setShowTextAI] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);

    // Text simulation states
    const [userInput, setUserInput] = useState("");
    const [sentText, setSentText] = useState("");
    const [aiResponse, setAiResponse] = useState("");
    const [step, setStep] = useState(0); // track animation step
    const [inputVisible, setInputVisible] = useState(true);
    const [clickingArrow, setClickingArrow] = useState(false);

    // Selected scenario
    const [scenario, setScenario] = useState({ userText: "", aiText: "" });

    const modelsData = {
        openai: { logo: "/logos/gpt.svg" },
        anthropic: { logo: "/logos/anthropic.svg" },
        gemini: { logo: "/logos/gemini.svg" },
        mistral: { logo: "/logos/mistral.svg" },
        llama: { logo: "/logos/meta.svg" },
        qwen: { logo: "/logos/qwen.svg" },
    };

    // Array of conversation scenarios with HTML tags
// Array of complex conversation scenarios with HTML tags
const scenarios = [
  {
    userText: 'Analyze the quarterly revenue and profit margins from the "<strong>Q4_financials</strong>" table for all products. Highlight the top 3 performing products, identify any anomalies in the data, and suggest actionable steps to improve underperforming products.',
    aiText: `<p>Here’s a detailed summary of the Q4 financial data from "<strong>Q4_financials</strong>":</p>
<ul>
  <li><strong>Product Alpha:</strong> Revenue $1,250,000, Profit Margin 32% (Top Performer)</li>
  <li><strong>Product Beta:</strong> Revenue $1,120,000, Profit Margin 28%</li>
  <li><strong>Product Gamma:</strong> Revenue $980,000, Profit Margin 24%</li>
</ul>
<p><strong>Observations:</strong> Delta and Epsilon products underperformed due to seasonal demand fluctuations and low marketing visibility. Product Beta shows unusually high returns in the last two weeks of the quarter—possibly due to a promotional campaign.</p>
<p><strong>Recommendations:</strong> Increase marketing spend for underperforming products, evaluate supply chain bottlenecks, and consider bundling promotions for Gamma and Delta to improve overall quarterly performance.</p>`,
  },
  {
    userText: 'Retrieve patient records from "<strong>Cardiology_Patients</strong>" and provide a detailed analysis of all patients with abnormal ECG readings over the past 6 months. Include trends in heart rate variability, common diagnoses, and recommend potential follow-up actions.',
    aiText: `<p>Based on the "<strong>Cardiology_Patients</strong>" database, the following patients had abnormal ECG readings in the last 6 months:</p>
<ul>
  <li>Patient ID: 1023 – Atrial fibrillation, HRV decreased by 15%, elevated risk of stroke</li>
  <li>Patient ID: 1056 – Ventricular tachycardia, HRV within normal range, requires immediate cardiology review</li>
  <li>Patient ID: 1089 – ST-segment elevation, HRV shows intermittent variability</li>
</ul>
<p><strong>Trends:</strong> Most patients with abnormal ECGs also show elevated blood pressure readings. There’s a notable pattern of increasing HRV variability in patients over 60.</p>
<p><strong>Recommendations:</strong> Schedule follow-up cardiology visits, review patient medication adherence, and consider advanced monitoring for high-risk patients. Lifestyle interventions like diet and exercise should also be advised.</p>`,
  },
  {
    userText: 'Generate a comprehensive analysis of recent marketing campaigns for Q2, including email, social media, and paid search. Provide insights on engagement rates, conversion performance, anomalies, and recommendations for optimizing ROI in the next quarter.',
    aiText: `<p>Here is a detailed analysis of Q2 marketing campaigns:</p>
<ul>
  <li><strong>Email Campaigns:</strong> Open rate 8%, Click-through rate 3%, conversion 1.2%. Engagement dropped 2% from Q1 due to lower subject line optimization.</li>
  <li><strong>Social Media Ads:</strong> Engagement 12%, Conversion 6%, highest click-through observed on video ads. Facebook showed declining ROI compared to Instagram.</li>
  <li><strong>Paid Search:</strong> CTR 10%, Conversion 4%, cost per acquisition increased by 15% due to higher competition in keywords.</li>
</ul>
<p><strong>Anomalies:</strong> Engagement spikes in social campaigns coincided with product launch announcements. Paid search performance dropped during the first week of the quarter.</p>
<p><strong>Recommendations:</strong> Focus on video content for social media, optimize email subject lines and personalization, adjust bidding strategy for paid search, and reallocate marketing budget towards the highest ROI channels.</p>`,
  },
  {
    userText: 'Analyze the performance of top-selling products for the last quarter using the "<strong>Sales_Q3</strong>" table. Include sales trends, revenue, regional performance, and highlight potential inventory or supply chain issues.',
    aiText: `<p>Here is the analysis of top-selling products from "<strong>Sales_Q3</strong>":</p>
<ul>
  <li><strong>Product X:</strong> Revenue $350,000, sales increasing steadily in North America, slight stock shortages in Europe.</li>
  <li><strong>Product Y:</strong> Revenue $310,000, high demand in APAC region, inventory sufficient.</li>
  <li><strong>Product Z:</strong> Revenue $290,000, declining sales in Latin America due to local competition.</li>
</ul>
<p><strong>Trends:</strong> Products X and Y consistently outperform Z globally. Seasonal trends indicate peak demand in July and September. Inventory bottlenecks are mostly in European warehouses for Product X.</p>
<p><strong>Recommendations:</strong> Reallocate stock to high-demand regions, optimize supply chain logistics, and consider promotional strategies in Latin America for Product Z.</p>`,
  },
  {
    userText: 'Write a Python script that connects to a PostgreSQL database, queries the "<strong>customer_orders</strong>" table for orders in the last month, and outputs a summary of total sales, top 5 products, and average order value.',
    aiText: `<p>Here is an example Python script to accomplish this:</p>
<pre><code>import psycopg2
from collections import Counter

conn = psycopg2.connect(
    dbname="mydatabase",
    user="username",
    password="password",
    host="localhost",
    port="5432"
)
cur = conn.cursor()
cur.execute("SELECT product_id, order_value FROM customer_orders WHERE order_date >= current_date - interval '1 month'")
rows = cur.fetchall()

total_sales = sum([row[1] for row in rows])
top_products = Counter([row[0] for row in rows]).most_common(5)
avg_order_value = total_sales / len(rows)

print(f"Total Sales: {total_sales}")
print("Top 5 Products:", top_products)
print(f"Average Order Value: {avg_order_value:.2f}")

cur.close()
conn.close()</code></pre>
<p>This script calculates total sales, identifies the top 5 products, and computes the average order value for the last month.</p>`,
  },
];



    // Intersection Observer to reset and pick scenario
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    setClickedCard(null);
                    setShowTextAI(false);
                    setFadeOut(false);
                    setUserInput("");
                    setSentText("");
                    setAiResponse("");
                    setStep(0);
                    setInputVisible(true);
                    setClickingArrow(false);
                    // Randomly pick a scenario
                    const randomIndex = Math.floor(Math.random() * scenarios.length);
                    setScenario(scenarios[randomIndex]);
                } else {
                    setVisible(false);
                }
            },
            { threshold: 0.4 }
        );
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Cards animation
    useEffect(() => {
        if (visible) {
            const totalCards = Object.keys(modelsData).length;
            const totalAnimationTime = totalCards * 0.2 * 1000 + 600;

            const timeout1 = setTimeout(() => {
                const randomIndex = Math.floor(Math.random() * totalCards);
                setClickedCard(randomIndex);

                const timeout2 = setTimeout(() => {
                    setClickedCard(null);
                    setFadeOut(true);

                    const timeout3 = setTimeout(() => {
                        setShowTextAI(true);
                        setStep(1); // start typing simulation
                    }, 400);

                    return () => clearTimeout(timeout3);
                }, 400);

                return () => clearTimeout(timeout2);
            }, totalAnimationTime);

            return () => clearTimeout(timeout1);
        }
    }, [visible]);

    // User typing
    useEffect(() => {
        if (step === 1) {
            let i = 0;
            const interval = setInterval(() => {
                if (i < scenario.userText.length) {
                    setUserInput(scenario.userText.slice(0, i + 1));
                    i++;
                } else {
                    clearInterval(interval);
                    setStep(2);
                }
            }, 20);
            return () => clearInterval(interval);
        }
    }, [step, scenario.userText]);

    // Simulate arrow click
    useEffect(() => {
        if (step === 2) {
            const timeout1 = setTimeout(() => {
                setClickingArrow(true); // start arrow click
                const timeout2 = setTimeout(() => {
                    setClickingArrow(false); // end arrow click
                    setSentText(scenario.userText);
                    setUserInput("");
                    setInputVisible(false);
                    setStep(3); // start AI typing after arrow animation
                }, 500); // duration of arrow click
                return () => clearTimeout(timeout2);
            }, 500); // wait 0.5s after typing before clicking
            return () => clearTimeout(timeout1);
        }
    }, [step, scenario.userText]);

    // AI typing
    useEffect(() => {
        if (step === 3) {
            let i = 0;
            const interval = setInterval(() => {
                if (i < scenario.aiText.length) {
                    setAiResponse(scenario.aiText.slice(0, i + 1));
                    i++;
                } else {
                    clearInterval(interval);
                    setStep(4);
                }
            }, 5);
            return () => clearInterval(interval);
        }
    }, [step, scenario.aiText]);

useEffect(() => {
  if (scrollRef.current) {
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }
}, [userInput, sentText, aiResponse]);

    return (
        <div ref={containerRef} className="animation_container">
            {!showTextAI ? (
                <div className={`model_choise_container ${fadeOut ? "fade_out" : ""}`}>
                    {Object.entries(modelsData).map(([key, model], index) => (
                        <div
                            key={key}
                            className={`model_card ${visible ? "fade_in" : ""} ${clickedCard === index ? "clicked" : ""
                                }`}
                            style={{ animationDelay: `${index * 0.2}s` }}
                        >
                            <img
                                src={model.logo}
                                alt={`${key} logo`}
                                className="model_card_logo"
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div ref={scrollRef} className="text_ai_anim">
                    {inputVisible ? (
                        <div className="text_ai_anim_user_mex_input">
                            <p className="text_ai_anim_user_mex_input_p" dangerouslySetInnerHTML={{ __html: userInput }}></p>
                            <div className="text_ai_anim_user_mex_input_footer">
                                <i
                                    className={`fa-solid fa-arrow-up ${clickingArrow ? "clicking_i" : ""
                                        }`}
                                ></i>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                className="text_ai_anim_user_mex"
                                dangerouslySetInnerHTML={{ __html: sentText }}
                            ></div>
                            <div
                                className="text_ai_anim_ai_mex"
                                dangerouslySetInnerHTML={{ __html: aiResponse }}
                            ></div>
                        </>
                    )}

                </div>
            )}
        </div>
    );
};

export default LLMsAnimation;
